/**
 * Generic settings surface for ANY registered tool. Looks up the ToolDef,
 * runs its declared scope + fields through the engine, and renders the shared
 * panel. This is the whole settings UI for every migrated tool — the Toolbar
 * just mounts `<ToolSettings toolId=… />`.
 */
import { SettingsPanel } from "./SettingsPanel";
import { FieldList } from "./FieldList";
import { useScopedSetting } from "./useScopedSetting";
import { toolById } from "./registry";
import styles from "./panel.module.css";

interface Props {
  toolId: string;
  open: boolean;
  onClose: () => void;
}

export function ToolSettings({ toolId, open, onClose }: Props) {
  const tool = toolById(toolId);
  const settings = tool?.settings;
  // Hooks run unconditionally; feed a harmless fallback when the tool has no
  // settings (then bail before rendering the panel).
  const sc = useScopedSetting(
    settings?.scope ?? { levels: "tab", perMode: true },
    settings?.fields ?? [],
    open,
  );
  if (!tool || !settings) return null;
  return (
    <SettingsPanel
      open={open}
      title={tool.label}
      scope={sc.scopeUi}
      actions={sc.actions}
      onCancel={() => {
        sc.cancel();
        onClose();
      }}
      onSave={() => {
        sc.save();
        onClose();
      }}
    >
      {sc.isInheriting ? (
        // Workspace scope, no per-chart override → inherit from User defaults.
        <div className={styles.inheritBanner}>
          <p className={styles.inheritText}>
            This chart is inheriting <strong>{tool.label}</strong> appearance
            from <strong>User defaults</strong>.
          </p>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={sc.customize}
          >
            Customize this chart
          </button>
        </div>
      ) : (
        <FieldList
          fields={settings.fields}
          value={sc.value}
          onChange={sc.setField}
        />
      )}
    </SettingsPanel>
  );
}
